import { CurrencyService } from "@/services/currency-service";

const intlcache: Record<string, Intl.NumberFormat> = {};
import type { Money } from "@/generated/graphql/b2c";
import { CurrencyConverter } from "@/util/currency-converter";
import { defaultCurrency } from "@/constants/currency";
import { defaultVatCoefficient } from "@/constants/vat";
import { Context } from "@/services/context.ts";

const settings = {
  defaultCentsDelimiter: ".",
  defaultGroupsDelimiter: ",",
  money: {
    style1: {
      cents: true,
      groupsDelimiter: " ",
      centsDelimiter: ".",
    },
    style2: {
      cents: true,
      groupsDelimiter: ",",
      centsDelimiter: ".",
    },
    style3: {
      cents: true,
      groupsDelimiter: "",
      centsDelimiter: ".",
    },
    style4: {
      cents: false,
      groupsDelimiter: ",",
      centsDelimiter: "",
    },
  },
};

export const FORMATTER_LISTENER_NAME = "formatter";

export class Formatter {
  currencyCode: string = defaultCurrency;
  vatCoefficient: number = defaultVatCoefficient;

  constructor(
    protected context: Context,
    protected currencyService: CurrencyService,
  ) {
    this.context.notify(FORMATTER_LISTENER_NAME);
  }

  setDefaultCurrency(currencyCode: string) {
    this.currencyCode = currencyCode;
    this.context.notify(FORMATTER_LISTENER_NAME);
  }

  get defaultCurrency() {
    return this.currencyCode;
  }

  setDefaultVat(vat: number) {
    this.vatCoefficient = vat;
    this.context.notify(FORMATTER_LISTENER_NAME);
  }

  price(amount: number, style = "style1" as "full" | "compact" | "compact2" | "style1" | "style2" | "style3" | "style4", isVatApplicable = true) {
    return this.formatMoney(this.getCustomerMoneyValue(amount, isVatApplicable), style, false);
  }

  getSeparateCurrencySymbolAndPrice(
    amount: number,
    style = "style1" as "full" | "compact" | "compact2" | "style1" | "style2" | "style3" | "style4",
    isVatApplicable = true,
  ) {
    const priceLine = this.formatMoney(this.getCustomerMoneyValue(amount, isVatApplicable), style, false);
    const currencySymbol = this.getCurrencySymbol(amount);
    const clearedPriceLine = priceLine.replace(currencySymbol, "");

    return { currencySymbol, price: clearedPriceLine };
  }

  getCurrencySymbol(amount: number) {
    const moneyValue = this.getCustomerMoneyValue(amount, false);
    const currency = moneyValue.currency;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      currencyDisplay: "narrowSymbol",
    });

    const parts = formatter.formatToParts(1);

    return parts.find((part) => part.type === "currency")?.value || "";
  }

  priceRange(
    amountRange: [number, number],
    style = "style1" as "full" | "compact" | "compact2" | "style1" | "style2" | "style3" | "style4",
    isVatApplicable = true,
  ) {
    const [from, to] = amountRange.map((amount) => this.getCustomerMoneyValue(amount, isVatApplicable));

    if (from.amount === to.amount) {
      return this.formatMoney(from, style, false);
    }

    return `${this.formatMoney(from, style, false)} - ${this.formatMoney(to, style, true)}`;
  }

  getBaseCurrencyValue(amount: number) {
    const currentCurrency = this.currencyCode ?? defaultCurrency;

    const currentMoneyValue = CurrencyConverter.packToMoney(amount, currentCurrency);

    const valueWithoutVat = CurrencyConverter.removeVat(currentMoneyValue, this.vatCoefficient);

    if (currentCurrency === defaultCurrency) {
      return valueWithoutVat;
    }

    const currentCurrencyRate = this.currencyService.getRate(currentCurrency);

    return CurrencyConverter.convertToBaseCurrency(valueWithoutVat, currentCurrency, currentCurrencyRate);
  }

  public getCurrentCurrencyCode() {
    return this.currencyCode ?? defaultCurrency;
  }

  public getCustomerMoneyValue(amount: number, isVatApplicable: boolean) {
    const currency = this.getCurrentCurrencyCode();

    const rateEntry = this.currencyService.getRate(currency);
    const moneyValue: Money = {
      amount: amount,
      currency: currency,
      cents: "",
    };

    let customerValue = CurrencyConverter.convert(moneyValue, currency, rateEntry);

    if (isVatApplicable) {
      customerValue = CurrencyConverter.applyVat(customerValue, this.vatCoefficient);
    }

    return customerValue;
  }

  public formatMoney(value: Money, style: "full" | "compact" | "compact2" | "style1" | "style2" | "style3" | "style4" = "style1", omitCurrency?: boolean) {
    try {
      const mode = this.getMode(style);
      const { cents, centsDelimiter, groupsDelimiter } = settings.money[mode];
      const { defaultCentsDelimiter, defaultGroupsDelimiter } = settings;

      const amount = this.getAmount(value);

      let formatted = this.moneyLine(value.currency, cents, amount, omitCurrency);

      if (centsDelimiter !== defaultCentsDelimiter) {
        formatted = formatted.replaceAll(defaultCentsDelimiter, centsDelimiter);
      }

      if (groupsDelimiter !== defaultGroupsDelimiter) {
        formatted = formatted.replaceAll(defaultGroupsDelimiter, groupsDelimiter);
      }

      return formatted;
    } catch (error) {
      console.error(error);
      return "--";
    }
  }

  public phoneCleaner(phone: string): string {
    return phone.replace(/[^0-9]/g, "");
  }

  private getMode(style: string) {
    switch (style) {
      case "style1":
      case "style2":
      case "style3":
      case "style4":
        return style as "style1" | "style2" | "style3" | "style4";
      default:
        return "style1" as const;
    }
  }

  private getAmount(value: Money) {
    const amount = value.amount === "" ? 0 : parseFloat(value.amount);
    if (!isNaN(amount)) {
      return amount;
    }
    const cents = parseInt(value.cents);
    if (!isNaN(cents)) {
      return cents / 100;
    }

    throw new Error(`incorrect money value: ${JSON.stringify(value)}`);
  }

  private moneyLine(currency: string, cents: boolean, floatval: number, omitCurrency?: boolean) {
    const key = `${currency}-${cents}-${omitCurrency}`;
    if (!intlcache[key]) {
      intlcache[key] = Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency,
        currencyDisplay: omitCurrency ? "code" : "narrowSymbol",
        maximumFractionDigits: cents ? undefined : 0,
      });
    }

    let formatted = intlcache[key].format(floatval);
    if (omitCurrency) {
      formatted = formatted.replace(`${currency}`, "").trim();
    }

    return formatted;
  }
}
